import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { supabase } from '../lib/supabase';
import type { DraftSession } from '../App';

const CHROME_WEB_STORE_URL = 'https://chromewebstore.google.com/detail/salifly-response-assistan/kjfhchpibmpfddkhihmjpjnmddbafaco';

interface DashboardMainProps {
  sessions: DraftSession[];
  query: string;
  onQuery: (q: string) => void;
  onDelete: (id: string) => Promise<void>;
}

function ChromeMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
      <circle cx="24" cy="24" r="9.5" fill="#fff" />
      <path d="M24 4a20 20 0 0 1 17.32 10H24a10 10 0 0 0-9.43 6.66L8.31 9.83A19.94 19.94 0 0 1 24 4Z" fill="#EA4335" />
      <path d="M4.6 16.51A20 20 0 0 0 22.27 43.9l6.26-10.83a10 10 0 0 1-13.99-6.26Z" fill="#34A853" />
      <path d="M44 24a20 20 0 0 1-21.73 19.9L32.5 26.4A10 10 0 0 0 32 18h12a20 20 0 0 1 0 6Z" fill="#FBBC05" />
    </svg>
  );
}

function dealStateClass(state: string) {
  const map: Record<string, string> = {
    Discovery:   'chip-discovery',
    Pitch:       'chip-pitch',
    Stalled:     'chip-stalled',
    Negotiation: 'chip-negotiation',
    Closing:     'chip-closing',
  };
  return map[state] ?? '';
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

type SignalStatus = 'replied' | 'used' | 'draft';

function getSignal(s: DraftSession): SignalStatus {
  if (s.lead_responded) return 'replied';
  const dh = s.draft_history?.[0];
  if (dh && typeof dh.selected_draft_index === 'number') return 'used';
  return 'draft';
}

const SIGNAL_LABEL: Record<SignalStatus, string>  = { replied: 'Replied', used: 'Draft used', draft: 'Not used' };
const SIGNAL_CLASS: Record<SignalStatus, string>  = { replied: 'doc-signal-dot--replied', used: 'doc-signal-dot--used', draft: 'doc-signal-dot--draft' };

// ── Deals Needing Attention (Feature #1 + dashboard side of #5) ──────────────
// Reuses the already-fetched DraftSession[] — no extra queries, no new columns.
const COLD_DAYS = 5;
const DAY_MS = 86_400_000;

function leadKey(s: DraftSession): string {
  // One row per lead. external_id isn't on DraftSession, so fall back to name+company.
  // Unnamed leads must NOT collapse together — key each on its own id instead.
  const name = (s.leads?.name ?? '').trim().toLowerCase();
  if (!name) return s.id;
  return name + '|' + (s.leads?.company ?? '').trim().toLowerCase();
}

/** Most-recent interaction per lead (sessions arrive newest-first from App.tsx). */
function latestPerLead(sessions: DraftSession[]): DraftSession[] {
  const seen = new Set<string>();
  const out: DraftSession[] = [];
  for (const s of sessions) {
    const k = leadKey(s);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

type AttentionBuckets = {
  owed: DraftSession[];     // they replied — you owe a response
  closing: DraftSession[];  // Closing / Negotiation — don't drop
  cold: DraftSession[];     // no touch in 5+ days, no reply
};

function computeAttention(sessions: DraftSession[]): AttentionBuckets {
  const now = Date.now();
  const owed: DraftSession[] = [];
  const closing: DraftSession[] = [];
  const cold: DraftSession[] = [];

  for (const s of latestPerLead(sessions)) {
    // A lead can land in at most one bucket — owed > closing > cold by priority.
    if (s.lead_responded) {
      owed.push(s);
      continue;
    }
    if (s.deal_state === 'Closing' || s.deal_state === 'Negotiation') {
      closing.push(s);
      continue;
    }
    const ageDays = (now - new Date(s.created_at).getTime()) / DAY_MS;
    if (ageDays >= COLD_DAYS) cold.push(s);
  }
  return { owed, closing, cold };
}

// ── ROI strip — visible value this calendar month ────────────────────────────
type RoiStats = {
  analyses: number;
  used: number;
  replyPct: number | null;
  savedLabel: string;
};

const MINUTES_SAVED_PER_DRAFT = 6;

function formatTimeSaved(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export const DashboardMain: React.FC<DashboardMainProps> = ({ sessions, query, onQuery, onDelete }) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [copiedId, setCopiedId]     = useState<string | null>(null);
  const [detail, setDetail]         = useState<DraftSession | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!openMenuId) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenuId]);

  const filtered = sessions.filter(s => {
    if (!query) return true;
    const lead = s.leads;
    return ((lead?.name ?? '') + (lead?.company ?? '')).toLowerCase().includes(query.toLowerCase());
  });

  const isEmpty = sessions.length === 0;

  const attention = computeAttention(sessions);
  const hasAttention = attention.owed.length + attention.closing.length + attention.cold.length > 0;

  // Exact server-side counts (head-only, RLS-scoped) — the sessions prop is
  // capped at 50 rows, which would undercount heavy months for Basic/Pro reps.
  // Re-fetched whenever the live subscription pushes new sessions.
  const [roi, setRoi] = useState<RoiStats | null>(null);
  useEffect(() => {
    const now = new Date();
    const monthStartIso = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    Promise.all([
      supabase.from('interactions').select('id', { count: 'exact', head: true })
        .gte('created_at', monthStartIso),
      supabase.from('interactions').select('id', { count: 'exact', head: true })
        .gte('created_at', monthStartIso).eq('lead_responded', true),
      supabase.from('draft_history').select('id', { count: 'exact', head: true })
        .gte('created_at', monthStartIso).not('selected_draft_index', 'is', null),
    ]).then(([a, r, u]) => {
      const analyses = a.count ?? 0;
      const replied  = r.count ?? 0;
      const used     = u.count ?? 0;
      setRoi({
        analyses,
        used,
        replyPct: analyses > 0 ? Math.round((replied / analyses) * 100) : null,
        savedLabel: formatTimeSaved(used * MINUTES_SAVED_PER_DRAFT),
      });
    });
  }, [sessions.length]);

  async function handleCopy(id: string, text: string) {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
    setOpenMenuId(null);
  }

  async function handleDelete(id: string) {
    setOpenMenuId(null);
    await onDelete(id);
  }

  return (
    <main className="main-content">
      <div className="main-container">
        {/* Hero Banner — always shown at the top */}
        <div className="hero-banner">
          <div className="hero-banner-content">
            <span className="hero-banner-eyebrow">Get the Salifly extension</span>
            <h2>Install Chrome extension</h2>
            <p>Install our Chrome extension to start analyzing leads and drafting messages right inside your CRM.</p>
          </div>
          <a
            href={CHROME_WEB_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hero-banner-cta"
          >
            <ChromeMark />
            <span>Get the app</span>
            <Icon name="arrow" size={16} />
          </a>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div className="action-bar-left">
            <h2>Responses</h2>
          </div>
          <div className="action-bar-right">
            <div className="search-field">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search by lead or company"
                value={query}
                onChange={e => onQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ROI strip — what Salifly delivered this month */}
        {!isEmpty && roi && (
          <div className="dash-roi">
            <div className="dash-roi-grid">
              <div className="dash-roi-card">
                <span className="dash-roi-value">{roi.analyses}</span>
                <span className="dash-roi-label">Analyses</span>
              </div>
              <div className="dash-roi-card">
                <span className="dash-roi-value">{roi.used}</span>
                <span className="dash-roi-label">Drafts used</span>
              </div>
              <div className="dash-roi-card">
                <span className="dash-roi-value">{roi.replyPct === null ? '—' : `${roi.replyPct}%`}</span>
                <span className="dash-roi-label">Reply rate</span>
              </div>
              <div className="dash-roi-card">
                <span className="dash-roi-value">{roi.savedLabel}</span>
                <span className="dash-roi-label">Est. time saved</span>
              </div>
            </div>
            <p className="dash-roi-caption">This month · time saved assumes ~{MINUTES_SAVED_PER_DRAFT} min per drafted reply</p>
          </div>
        )}

        {/* Deals Needing Attention — Features #1 + #5 */}
        {hasAttention && (
          <div className="attention-section">
            <div className="docs-header">Needs attention</div>
            <div className="attention-grid">
              <AttentionGroup
                tone="owed"
                icon="send"
                title="You owe a response"
                caption="They replied — close the loop"
                sessions={attention.owed}
                onSelect={setDetail}
              />
              <AttentionGroup
                tone="closing"
                icon="check"
                title="Don't drop these"
                caption="Closing & negotiation in flight"
                sessions={attention.closing}
                onSelect={setDetail}
              />
              <AttentionGroup
                tone="cold"
                icon="clock"
                title="Going cold"
                caption={`No touch in ${COLD_DAYS}+ days`}
                sessions={attention.cold}
                onSelect={setDetail}
              />
            </div>
          </div>
        )}

        {/* Draft Cards */}
        <div className="docs-section">
          {!isEmpty && <div className="docs-header">Recent</div>}
          <div className="doc-grid">
            {isEmpty ? (
              <div className="drafts-empty-state">
                <Icon name="send" size={32} />
                <h3>No responses yet</h3>
                <p>Analyze a lead with your Salifly extension and your AI-generated drafts will appear here.</p>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ color: 'var(--fg-3)', gridColumn: '1 / -1', padding: '40px 0' }}>
                No drafts match your search.
              </div>
            ) : filtered.map(s => {
              const name      = s.leads?.name ?? 'Unknown';
              const company   = s.leads?.company ?? '';
              const draftData = s.draft_history?.[0]?.ai_drafts;
              const responses = draftData?.responses ?? [];

              // Which draft was selected (if any)
              const selectedIdx = s.draft_history?.[0]?.selected_draft_index ?? null;
              const activeDraft = selectedIdx !== null && responses[selectedIdx]
                ? responses[selectedIdx]
                : responses[0] ?? null;

              // Tone label from the selected (or first) draft
              const toneLabel = activeDraft?.tone ?? null;

              const signal = getSignal(s);
              const isOpen = openMenuId === s.id;

              return (
                <div key={s.id} className="doc-card fade-in">
                  {/* Card head: deal state + signal + menu */}
                  <div className="doc-card-head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className={`doc-badge ${dealStateClass(s.deal_state)}`}>{s.deal_state}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }} ref={isOpen ? menuRef : null}>
                      {/* Signal indicator */}
                      <div className="doc-signal">
                        <span className={`doc-signal-dot ${SIGNAL_CLASS[signal]}`} />
                        <span className="doc-signal-label">{SIGNAL_LABEL[signal]}</span>
                      </div>
                      <button
                        className={`btn btn-ghost btn-sm btn-icon${isOpen ? ' active' : ''}`}
                        onClick={e => { e.stopPropagation(); setOpenMenuId(isOpen ? null : s.id); }}
                        aria-label="Card actions"
                      >
                        <Icon name="menu" size={16} />
                      </button>
                      {isOpen && (
                        <div className="card-menu-dropdown">
                          <button
                            className="card-menu-item"
                            onClick={() => handleCopy(s.id, activeDraft?.text ?? '')}
                            disabled={!activeDraft}
                          >
                            <Icon name={copiedId === s.id ? 'check' : 'clipboard'} size={14} />
                            {copiedId === s.id ? 'Copied!' : 'Copy draft'}
                          </button>
                          <button
                            className="card-menu-item card-menu-item--danger"
                            onClick={() => handleDelete(s.id)}
                          >
                            <Icon name="trash" size={14} />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Lead name + company — click opens the detail modal */}
                  <div
                    className="doc-card-body doc-card-body--clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => setDetail(s)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail(s); } }}
                  >
                    <h3 className="doc-title">{name}{company ? <span className="doc-company"> · {company}</span> : null}</h3>
                    {activeDraft ? (
                      <p className="doc-snippet">{activeDraft.text}</p>
                    ) : (
                      <p className="doc-snippet doc-snippet--empty">No draft captured.</p>
                    )}
                  </div>

                  {/* Footer: tone label + platform + timestamp */}
                  <div className="doc-card-foot">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {toneLabel && <span className="doc-tone-chip">{toneLabel}</span>}
                      {s.platform && <span className="doc-platform">{s.platform}</span>}
                    </div>
                    <span>{timeAgo(s.created_at)} ago</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {detail && <AttentionDetailModal session={detail} onClose={() => setDetail(null)} />}
    </main>
  );
};

// ── Attention group card (Features #1 + #5) ──────────────────────────────────
type AttentionTone = 'owed' | 'closing' | 'cold';

const AttentionGroup: React.FC<{
  tone: AttentionTone;
  icon: 'send' | 'check' | 'clock';
  title: string;
  caption: string;
  sessions: DraftSession[];
  onSelect: (s: DraftSession) => void;
}> = ({ tone, icon, title, caption, sessions, onSelect }) => {
  const empty = sessions.length === 0;
  return (
    <div className={`attention-card attention-card--${tone}${empty ? ' attention-card--empty' : ''}`}>
      <div className="attention-card-head">
        <span className="attention-icon"><Icon name={icon} size={15} /></span>
        <div className="attention-head-text">
          <span className="attention-title">{title}</span>
          <span className="attention-caption">{caption}</span>
        </div>
        <span className="attention-count">{sessions.length}</span>
      </div>
      {empty ? (
        <p className="attention-empty">All clear here.</p>
      ) : (
        <ul className="attention-list">
          {sessions.slice(0, 4).map(s => (
            <li key={s.id}>
              <button type="button" className="attention-item attention-item--clickable" onClick={() => onSelect(s)}>
                <span className="attention-item-name">{s.leads?.name ?? 'Unknown'}</span>
                {s.leads?.company && <span className="attention-item-company">{s.leads.company}</span>}
                <span className="attention-item-age">{timeAgo(s.created_at)}</span>
                <Icon name="arrow" size={12} />
              </button>
            </li>
          ))}
          {sessions.length > 4 && (
            <li className="attention-item attention-item--more">+{sessions.length - 4} more</li>
          )}
        </ul>
      )}
    </div>
  );
};

// ── Attention detail modal — shows the lead's captured draft + context ───────
const AttentionDetailModal: React.FC<{
  session: DraftSession;
  onClose: () => void;
}> = ({ session, onClose }) => {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const name      = session.leads?.name ?? 'Unknown';
  const company   = session.leads?.company ?? '';
  const dh        = session.draft_history?.[0];
  const drafts    = dh?.ai_drafts;
  const responses = drafts?.responses ?? [];
  const selected  = dh?.selected_draft_index ?? null;

  async function copy(text: string, idx: number) {
    await navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1800);
  }

  return (
    <div className="attention-overlay" onMouseDown={onClose}>
      <div className="attention-modal" onMouseDown={e => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose} aria-label="Close">
          <Icon name="close" size={16} />
        </button>

        <div className="attention-modal-head">
          <span className={`doc-badge ${dealStateClass(session.deal_state)}`}>{session.deal_state}</span>
          <h3 className="attention-modal-title">
            {name}{company ? <span className="doc-company"> · {company}</span> : null}
          </h3>
          <p className="attention-modal-meta">
            {session.platform && <span>{session.platform}</span>}
            <span>{timeAgo(session.created_at)} ago</span>
            {session.lead_responded && <span className="attention-modal-flag">Lead replied</span>}
          </p>
        </div>

        {responses.length === 0 ? (
          <p className="attention-modal-empty">No draft was captured for this lead.</p>
        ) : (
          <div className="attention-modal-drafts">
            {responses.map((r, idx) => (
              <div key={idx} className={`attention-draft${selected === idx ? ' attention-draft--selected' : ''}`}>
                <div className="attention-draft-head">
                  <span className="doc-tone-chip">{r.tone}</span>
                  {selected === idx && <span className="attention-draft-used">Used</span>}
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(r.text, idx)}>
                    <Icon name={copiedIdx === idx ? 'check' : 'clipboard'} size={13} />
                    {copiedIdx === idx ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="attention-draft-text">{r.text}</p>
              </div>
            ))}
          </div>
        )}

        {drafts?.suggested_next_steps && drafts.suggested_next_steps.length > 0 && (
          <div className="attention-modal-steps">
            <span className="attention-modal-steps-label">Suggested next steps</span>
            <ul>
              {drafts.suggested_next_steps.map((step, i) => <li key={i}>{step}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
